import cx from 'classnames';
import { canUseDOM } from 'exenv';
import { t } from 'i18next';
import { Trans } from 'react-i18next';

import { Stack } from '../../../../../../../shared/frontend/components/stack/Stack';
import { asset, scaledAssetUrl } from '../../../../../../../shared/frontend/services/assetResolverService';
import { formatMsShort } from '../../../../../../../shared/frontend/libs/utils';
import CountdownTimer from '../../../../../../../shared/frontend/components/presentational/countdownTimer';
import { TGuildRankingSummary } from '../../../../../shared/types/guildTypes';
import { namedRoutes, resolveRoutePath } from '../../../bundles/routes';
import { GuildRank } from '../../presentational/guildRank';
import { Link } from '../../../../../../../shared/frontend/components/link';
import { CurrentAttemptProgress } from '../../presentational/raidRankingsElements/CurrentAttemptProgress';
import { GuildCard } from '../../presentational/raidRankingsElements/GuildCard';
import { ProgressBadge } from '../../presentational/raidRankingsElements/ProgressBadge';
import { RioSelect } from '../../../../../../../shared/frontend/components/presentational/select/Select';

export function GuildRankingsTableFrontpage(props: any) {
  const isDifficultyControlled = props.setDifficulty !== undefined;

  const { realm } = props;
  const linkParams = {
    raid: props.raid.slug,
    difficulty: props.difficulty,
    region: props.region.slug,
    ...(realm ? { realm: realm.slug ? realm.slug : realm } : {})
  };

  const colspan = 4;
  const imageSize = 24;

  const guilds = props.guilds || [];
  const hasData = guilds.length > 0;
  const isPartition = !!props.partition;
  const isNotYetOpen = isPartition && props.partitionOpensAt && new Date(props.partitionOpensAt) > new Date();

  const partitionBgImage = isPartition
    ? `url(${asset(`/images/${props.raid.slug}/headers/partitions/${props.partition}.jpg`, { width: 700 })})`
    : undefined;

  // When partition has data: bg image only on the header
  // When partition is empty: bg image spans the whole table
  const tableBgStyle = isPartition && !hasData
    ? {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.85), transparent 150px), ${partitionBgImage}`,
        backgroundSize: 'auto, cover',
        backgroundPosition: 'top, center center'
      }
    : undefined;

  const theadBgStyle = isPartition
    ? {
        background: 'transparent',
        ...(hasData
          ? {
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), ${partitionBgImage}`,
              backgroundSize: 'auto, cover',
              backgroundPosition: 'top, center center'
            }
          : {})
      }
    : undefined;

  return (
    <table
      className={cx('slds-table', 'slds-table--bordered', 'slds-table--striped', 'rio-rankings-table')}
      style={{
        ...props.style,
        ...tableBgStyle
      }}
    >
      <thead style={theadBgStyle}>
        <tr>
          <th
            colSpan={colspan}
            className="slds-cell-wrap text-white "
            style={isPartition ? { background: 'transparent' } : undefined}
          >
            {isDifficultyControlled ? (
              <Stack justifyContent="space-between">
                <div style={{ minWidth: 80 }} />
                <div className="slds-text-align--center slds-text-heading--small">{props.title}</div>
                <Stack justifyContent="flex-end" style={{ minWidth: 80 }}>
                  {isDifficultyControlled && (
                    <RioSelect
                      options={['mythic', 'heroic', 'normal']}
                      getOptionLabel={(option) => t(`common:difficulty.${option}.name`)}
                      value={props.difficulty}
                      onChange={props.setDifficulty}
                      size="xx-small"
                      variant="borderless"
                      isSearchable={false}
                      valueColor="raiderio"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          minWidth: 100,
                          position: 'absolute',
                          right: 0
                        })
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            ) : (
              <div className="slds-text-align--center slds-text-heading--small">{props.title}</div>
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {guilds.length === 0 && (
          <tr>
            <td
              colSpan={colspan}
              className={isPartition ? undefined : 'rio-background--header'}
              style={
                isPartition
                  ? { background: 'transparent' }
                  : {
                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${asset(
                        `/images/${props.raid.slug}/headers/_zone.jpg`,
                        {
                          width: 1000,
                          quality: 75
                        }
                      )})`
                    }
              }
            >
              <div
                className="slds-p-around--large slds-text-align--center slds-align--absolute-center slds-text-heading--large"
                style={{
                  minHeight: '250px',
                  whiteSpace: 'normal'
                }}
              >
                {isNotYetOpen ? (
                  <div className="text-bold rio-text-shadow--normal">
                    <Trans i18nKey="frontpage:raid-unlocks-in">
                      <CountdownTimer
                        style={{ display: 'block' }}
                        to={props.partitionOpensAt}
                        formatter={formatMsShort}
                        onComplete={
                          canUseDOM
                            ? window.location.reload.bind(window.location)
                            : () => {
                                /* nop */
                              }
                        }
                      />
                    </Trans>
                  </div>
                ) : (
                  t('common:errors.empty-guild-leaderboard')
                )}
              </div>
            </td>
          </tr>
        )}
        {guilds.map((guild: TGuildRankingSummary) => {
          return (
            <tr key={guild.guild.id} className={`faction--${guild.guild.faction}`}>
              <td data-label="Rank" className="slds-text-align--center">
                <GuildRank rank={guild.rank} small={true} />
              </td>
              <td data-label="Guild" className="slds-cell-wrap">
                <Stack alignItems="center" spacingInRems={6}>
                  <div className="image-wrapper-width-full" style={{ width: imageSize, flexShrink: 0 }}>
                    <img
                      src={
                        guild.guild.logo
                          ? scaledAssetUrl(guild.guild.logo, { width: imageSize * 2 })
                          : asset(`/images/site/${guild.guild.faction}_icon4.png`)
                      }
                      alt={`${guild.guild.name} logo`}
                    />
                  </div>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <GuildCard
                      guild={guild.guild}
                      raid={props.raid}
                      difficulty={props.difficulty}
                      streamers={guild.streamers}
                      recruitmentProfiles={guild.recruitmentProfiles}
                    />
                  </div>
                </Stack>
              </td>
              <td className="slds-text-align--left">
                <CurrentAttemptProgress
                  size="medium"
                  attempts={guild.attempts || []}
                  guild={guild.guild}
                  guildPrivacy={guild.guildPrivacy}
                  difficulty={props.difficulty}
                  raid={props.raid}
                  bare={false}
                />
              </td>
              <td className="slds-text-align--right">
                <ProgressBadge
                  title="Raid Progress"
                  numDefeated={guild.encountersDefeated.length}
                  totalBosses={props.raid.encounters.length}
                  difficulty={props.difficulty}
                  visible={props.raid.encounters.length > 0}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
      {!isPartition && (
        <tfoot>
          <tr>
            <td colSpan={colspan} className="slds-text-align--center slds-text-body--small">
              {props.recent ? (
                <span>&nbsp;</span>
              ) : (
                <Link
                  className="rio-realm-link--muted"
                  to={resolveRoutePath(
                    props.realm ? namedRoutes.RaidRealmRankings : namedRoutes.RaidRankings,
                    linkParams
                  )}
                >
                  {t('common:leaderboard.view-leaderboard-prompt')}
                </Link>
              )}
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}
